(() => {
  'use strict';
  const root = document.querySelector('[data-fp-finder]');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const el = name => root.querySelector(`[data-finder-${name}]`);
  const state = { products: [], selected: new Set(), request: null, revision: 0, file: null, referenceURL: null, adding: false, cartUncertain: false };
  const currency = new Intl.NumberFormat(root.dataset.locale || 'en', { style: 'currency', currency: root.dataset.currency || 'USD' });
  const visibleProducts = () => state.products.filter(p => !el('quickship').checked || p.dataset.quickship === 'true');
  const eligible = p => Boolean(p.dataset.variant) && /^\d+$/.test(p.dataset.variant) && Number.isSafeInteger(Number(p.dataset.price));
  const selectedProducts = () => state.products.filter(p => state.selected.has(p.dataset.variant));

  function message(text, { loading = false, retry = false } = {}) {
    el('message').hidden = !text;
    el('message-text').textContent = text;
    el('spinner').hidden = !loading;
    el('retry').hidden = !retry;
    el('matches').setAttribute('aria-busy', String(loading));
  }

  function globalError(text) {
    el('error').textContent = text;
    el('error').hidden = !text;
    if (text) el('error').scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }

  function updateSelection() {
    const selected = selectedProducts();
    state.products.forEach(product => {
      const checked = state.selected.has(product.dataset.variant);
      product.classList.toggle('is-selected', checked);
      product.querySelector('[data-finder-pick]').checked = checked;
    });
    const shown = visibleProducts().filter(eligible);
    el('all').checked = shown.length > 0 && shown.every(p => state.selected.has(p.dataset.variant));
    el('all').indeterminate = !el('all').checked && shown.some(p => state.selected.has(p.dataset.variant));
    el('all').disabled = !shown.length;
    el('cart-bar').hidden = !selected.length;
    root.classList.toggle('has-selection', selected.length > 0);
    el('selected-count').textContent = `${selected.length} fabric${selected.length === 1 ? '' : 's'} selected`;
    el('total').textContent = currency.format(selected.reduce((sum, p) => sum + Number(p.dataset.price), 0) / 100);
    el('selected-images').replaceChildren(...selected.slice(0, 3).map(product => {
      const img = document.createElement('img');
      const source = product.querySelector('.fp-card__ph img');
      if (source) img.src = source.currentSrc || source.src;
      img.alt = '';
      return img;
    }));
    el('add').disabled = !selected.length || state.adding;
  }

  function render() {
    const shown = visibleProducts();
    const sort = el('sort').value;
    shown.sort((a, b) => {
      if (sort === 'newest') return Number(b.dataset.published) - Number(a.dataset.published);
      if (sort === 'price-up' || sort === 'price-down') {
        if (!eligible(a) || !eligible(b)) return Number(eligible(b)) - Number(eligible(a));
        return (Number(a.dataset.price) - Number(b.dataset.price)) * (sort === 'price-down' ? -1 : 1);
      }
      return Number(a.dataset.rank) - Number(b.dataset.rank);
    });
    el('grid').replaceChildren(...shown);
    el('count').textContent = `${shown.length} Result${shown.length === 1 ? '' : 's'}`;
    el('toolbar').hidden = !state.products.length;
    message(shown.length ? '' : (state.products.length ? 'None of these matches have 24-hour sample dispatch. Turn off the filter to see all matches.' : 'No matching fabrics are currently available in our shop. Try a closer photo, or contact our sourcing team.'));
    updateSelection();
  }

  async function prepareImage(file) {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) throw new Error('Please choose a JPG, PNG or WebP image.');
    if (!file.size || file.size > 10 * 1024 * 1024) throw new Error('Please choose an image smaller than 10 MB.');
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      if (!img.naturalWidth || !img.naturalHeight || img.naturalWidth * img.naturalHeight > 40_000_000) throw new Error('Please choose an image under 40 megapixels.');
      const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', .9));
      if (!blob) throw new Error('This image could not be opened. Please choose another image.');
      return blob;
    } finally { URL.revokeObjectURL(url); }
  }

  async function hydrate(handles, signal) {
    const groups = [];
    for (let i = 0; i < handles.length; i += 20) groups.push(handles.slice(i, i + 20));
    const batches = await Promise.all(groups.map(async group => {
      const url = new URL(root.dataset.searchUrl, window.location.origin);
      url.searchParams.set('q', group.join('|'));
      url.searchParams.set('section_id', 'fivepoint-finder-products');
      const response = await fetch(url, { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Product details could not be loaded. Please try again.');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!doc.querySelector('[data-finder-product-batch]')) throw new Error('Product details are unavailable. Please try again.');
      return [...doc.querySelectorAll('[data-finder-product]')];
    }));
    const products = batches.flat();
    // Only accept exact requested handles, one card per published product.
    const byHandle = new Map(products.filter(p => handles.includes(p.dataset.handle)).map(p => [p.dataset.handle, p]));
    return handles.flatMap((handle, rank) => {
      const product = byHandle.get(handle);
      if (!product) return [];
      product.dataset.rank = String(rank);
      return [document.importNode(product, true)];
    });
  }

  async function search(file) {
    if (state.adding) return;
    const revision = ++state.revision;
    state.request?.abort();
    const controller = new AbortController();
    state.request = controller;
    let timer;
    let timedOut = false;
    const started = performance.now();
    globalError('');
    try {
      const image = await prepareImage(file);
      if (revision !== state.revision || state.adding) return;
      state.file = file;
      state.products = [];
      state.selected.clear();
      state.cartUncertain = false;
      el('add').querySelector('span').textContent = 'Add selected to cart';
      el('cart-error').hidden = true;
      el('quickship').checked = false;
      el('sort').value = 'similar';
      el('grid').replaceChildren();
      updateSelection();
      if (state.referenceURL) URL.revokeObjectURL(state.referenceURL);
      state.referenceURL = URL.createObjectURL(image);
      el('reference').src = state.referenceURL;
      el('landing').hidden = true;
      el('results').hidden = false;
      el('results-crumb').hidden = false;
      el('toolbar').hidden = true;
      el('notice').hidden = true;
      el('heading').textContent = 'Your fabric matches';
      el('subtitle').textContent = 'Select fabrics to add colour cards to your cart.';
      message('Finding fabrics with similar textures, patterns and colours…', { loading: true });
      timer = setTimeout(() => { timedOut = true; controller.abort(); }, 90000);
      const form = new FormData();
      form.append('file', image, 'fabric-reference.jpg');
      const response = await fetch(root.dataset.searchEndpoint, { method: 'POST', body: form, signal: controller.signal, credentials: 'omit' });
      if (!response.ok) {
        if (response.status === 429) throw new Error('The Fabric Finder is busy. Please try again in a moment.');
        if (response.status === 413 || response.status === 422) throw new Error('This image could not be searched. Please try a different photo.');
        throw new Error('The Fabric Finder could not complete your search. Please try again.');
      }
      const result = await response.json();
      if (!Array.isArray(result.handles) || result.handles.length > 40 || result.handles.some(handle => typeof handle !== 'string' || !/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle))) throw new Error('The search response was incomplete. Please try again.');
      const handles = [...new Set(result.handles)];
      const products = await hydrate(handles, controller.signal);
      if (revision !== state.revision) return;
      state.products = products;
      render();
      window.FivePointEvents?.emit('fp_finder_result',{result:products.length?'success':'empty',result_count:products.length,duration_ms:Math.round(performance.now()-started)},{source_surface:'finder',component_id:'finder-results'});
      if (products.length < handles.length) {
        el('notice').textContent = 'Some matches are no longer available in this shop.';
        el('notice').hidden = false;
      }
    } catch (error) {
      if (revision !== state.revision) return;
      if (error.name === 'AbortError' && !timedOut) return;
      window.FivePointEvents?.emit('fp_interaction_error',{operation:'finder',error_code:timedOut?'timeout':error instanceof TypeError?'network':'upstream'},{source_surface:'finder',component_id:'finder-search'});
      const text = timedOut ? 'The search is taking too long. Please try again.' : (error instanceof TypeError ? 'We could not connect. Check your connection and try again.' : error.message);
      if (!el('results').hidden && state.file === file) message(text, { retry: true });
      else globalError(text);
    } finally { clearTimeout(timer); }
  }

  async function addSelected() {
    if (state.adding) return;
    const bag = document.getElementById('cart-drawer');
    if (!bag || typeof bag.addItems !== 'function') {
      el('cart-error').textContent = 'The shopping bag has not loaded. Please refresh the page before adding colour cards.';
      el('cart-error').hidden = false;
      return;
    }
    if (state.cartUncertain) { await bag.refreshAndOpen(); return; }
    const items = selectedProducts().filter(eligible).map(p => ({ id: p.dataset.variant, quantity: 1 }));
    if (!items.length) return;
    state.adding = true;
    el('add').disabled = true;
    el('add').querySelector('span').textContent = 'Adding colour cards…';
    el('cart-error').hidden = true;
    root.querySelectorAll('[data-finder-choose], [data-finder-pick], [data-finder-all], [data-finder-clear]').forEach(button => { button.disabled = true; });
    try {
      await bag.addItems(items,{source_surface:'finder',component_id:'finder-batch-add'});
      state.selected.clear();
      el('add').querySelector('span').textContent = 'Add selected to cart';
    } catch (error) {
      // A lost response (or partial rejection) must never trigger a duplicate add.
      state.cartUncertain = true;
      el('cart-error').textContent = `${error.name === 'AbortError' || error instanceof TypeError ? 'We could not confirm the cart update.' : error.message} Please review your cart before trying again.`;
      el('cart-error').hidden = false;
      el('add').querySelector('span').textContent = 'Review cart';
    } finally {
      state.adding = false;
      root.querySelectorAll('[data-finder-choose], [data-finder-clear]').forEach(button => { button.disabled = false; });
      state.products.forEach(product => { product.querySelector('[data-finder-pick]').disabled = !eligible(product); });
      updateSelection();
    }
  }

  root.querySelectorAll('[data-finder-choose]').forEach(button => button.addEventListener('click', () => el('file').click()));
  el('file').addEventListener('change', () => { const file = el('file').files[0]; if (file) search(file); el('file').value = ''; });
  const drop = el('drop');
  ['dragenter', 'dragover'].forEach(name => drop.addEventListener(name, event => { event.preventDefault(); drop.classList.add('is-dragging'); }));
  ['dragleave', 'drop'].forEach(name => drop.addEventListener(name, event => { event.preventDefault(); drop.classList.remove('is-dragging'); }));
  drop.addEventListener('drop', event => { if (event.dataTransfer.files[0]) search(event.dataTransfer.files[0]); });
  el('retry').addEventListener('click', () => { if (state.file) search(state.file); });
  el('grid').addEventListener('change', event => {
    if (!event.target.matches('[data-finder-pick]') || state.adding) return;
    const product = event.target.closest('[data-finder-product]');
    if (!eligible(product)) return;
    if (event.target.checked) state.selected.add(product.dataset.variant);
    else state.selected.delete(product.dataset.variant);
    updateSelection();
  });
  el('all').addEventListener('change', () => {
    if (state.adding) return;
    visibleProducts().filter(eligible).forEach(product => { if (el('all').checked) state.selected.add(product.dataset.variant); else state.selected.delete(product.dataset.variant); });
    updateSelection();
  });
  el('clear').addEventListener('click', () => { if (!state.adding) { state.selected.clear(); updateSelection(); } });
  el('quickship').addEventListener('change', render);
  el('sort').addEventListener('change', render);
  el('add').addEventListener('click', addSelected);
})();
